import { createPortal } from 'preact/compat';
import { useState } from 'preact/hooks';
import { AddBook } from '../add/AddBook';
import { APP_VERSION } from '../lib/version';
import { Link } from '../lib/router';
import { AccountMenu } from './AccountMenu';
import { FindBox } from './FindBook';
import { useFlag, useMatches, useUpdated } from './hooks';

/** Below this the header's search box says just "Search…" (upright iPad). */
const SHORT_SEARCH = '(max-width: 900px)';
import { HOME, SECTIONS, type Section } from './sections';

// The header and section bar (decisions doc › Header, Phone navigation; boards 2–5).
// Laptop: name, the five-item bar, search, Updated just now, version (small text, Kristi
// Oct 26), + Add a book, account circle. Phone: a top bar with the section's title, search,
// + and the account circle; the five-item bar pinned at the bottom of every screen.
// Search shows while the book page is on, + Add a book while book_page.add is on (R11).

const toTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

/** + Add a book: the Add a book panel; hidden while book_page.add is off (R11: nothing says what isn't built). */
function useAdd() {
  const on = useFlag('book_page.add') === true;
  const [open, setOpen] = useState(false);
  return {
    on,
    open: () => setOpen(true),
    // Over everything (the header is its own layer under the book's side panel).
    panel: open ? createPortal(<AddBook onClose={() => setOpen(false)} />, document.body) : null,
  };
}

/** The header search: finds books on your list (title, author or B-number); hidden while the book page is off. */
function useSearch() {
  const finds = useFlag('book_page') === true;
  const [sheet, setSheet] = useState(false);
  return { finds, sheet, open: () => setSheet(true), close: () => setSheet(false) };
}

/** The search as a sheet under the header (phone, and narrow laptop windows). */
function FindSheet({ onClose }: { onClose: () => void }) {
  return (
    <div class="find-sheet" role="dialog" aria-label="Find a book">
      <FindBox inputId="find-sheet-q" autoFocus onDone={onClose} />
      <button type="button" class="btn-secondary" onClick={onClose}>
        Close
      </button>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">
      <circle cx="9" cy="9" r="6" />
      <path d="M14 14l4 4" />
    </svg>
  );
}

export function LaptopHeader({ current, email, isAdmin }: { current: Section; email: string; isAdmin: boolean }) {
  const updated = useUpdated();
  const search = useSearch();
  const adding = useAdd();
  const short = useMatches(SHORT_SEARCH);
  return (
    <header class="hdr">
      {/* Updated and the version: small quiet text along the top right (Kristi, Oct 26); 10 px in
          N5 with no dot (Kristi, Oct 27: still too prominent). */}
      <div class="hdr-top">
        <span class="hdr-updated" title="When this page last loaded your list">
          {updated}
        </span>
        <span class="hdr-version">{APP_VERSION}</span>
      </div>
      <div class="hdr-inner">
        <Link href={HOME.path} class="hdr-name">
          Reading List
        </Link>
        <nav aria-label="Sections" class="hdr-nav">
          {SECTIONS.map((s) => (
            <Link
              key={s.key}
              href={s.path}
              class="hdr-nav-item"
              aria-current={s.key === current.key ? 'page' : undefined}
              onSame={toTop}
            >
              {s.name}
            </Link>
          ))}
        </nav>
        {/* Search is always a text box on laptops and iPads; it narrows with the window. */}
        <div class="hdr-searchwrap">
          {search.finds ? (
            <div class="hdr-find">
              <FindBox inputId="hdr-q" placeholder={short ? 'Search…' : 'Find a book on your list'} />
            </div>
          ) : null}
        </div>
        <div class="hdr-tools">
          {adding.on ? (
            <>
              <button type="button" class="btn-main hdr-add" onClick={adding.open}>
                + Add a book
              </button>
              <button type="button" class="round-btn round-main hdr-add-round" aria-label="Add a book" onClick={adding.open}>
                +
              </button>
            </>
          ) : null}
          <AccountMenu email={email} isAdmin={isAdmin} />
        </div>
      </div>
      {adding.panel}
    </header>
  );
}

/** The phone's search button with its sheet (the top bar, and the book page's own top bar). */
export function PhoneSearch() {
  const search = useSearch();
  if (!search.finds) return null;
  return (
    <>
      <button type="button" class="round-btn ptop-search" aria-label="Search" onClick={search.open}>
        <SearchIcon />
      </button>
      {search.sheet ? <FindSheet onClose={search.close} /> : null}
    </>
  );
}

export function PhoneTopBar({ title, email, isAdmin }: { title: string; email: string; isAdmin: boolean }) {
  // No Updated dot on the phone's top bar (Kristi, Nov 1: it read as a stray mark beside the name).
  const search = useSearch();
  const adding = useAdd();
  return (
    <header class="ptop">
      <h1 class="ptop-title">{title}</h1>
      {/* The tools sit at the right, whichever of them are on. */}
      <span class="ptop-tools">
        {search.finds ? (
          <button type="button" class="round-btn ptop-search" aria-label="Search" onClick={search.open}>
            <SearchIcon />
          </button>
        ) : null}
        {adding.on ? (
          <button type="button" class="round-btn round-main" aria-label="Add a book" onClick={adding.open}>
            +
          </button>
        ) : null}
      </span>
      <AccountMenu email={email} isAdmin={isAdmin} />
      {search.sheet ? <FindSheet onClose={search.close} /> : null}
      {adding.panel}
    </header>
  );
}

/** Pinned at the bottom on a phone, on section pages and shared pages alike. */
export function SectionBar({ current }: { current: Section }) {
  return (
    <nav aria-label="Sections" class="sbar">
      {SECTIONS.map((s) => (
        <Link
          key={s.key}
          href={s.path}
          class="sbar-item"
          aria-current={s.key === current.key ? 'page' : undefined}
          onSame={toTop}
        >
          <span class="sbar-mark" aria-hidden="true" />
          {s.name}
        </Link>
      ))}
    </nav>
  );
}
